import { Col, Row } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';

import { ButtonColorTypes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SecondaryButtonLoading from '~src/shared/buttons/loadingButtons/SecondaryButtonLoading';
import CustomFormInput from '~src/shared/form/inputs/FormInput';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Scale, ScaleVisualization } from '~src/interfaces/scale.interface';
import { IconContainer } from '~src/modules/topics/components/details/scales/topicScaleAdd.sc';
import { StoreI } from '~src/rootReducer';
import ScaleComponent from '~src/shared/features/scale/Scale';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import * as SC from './headers/headers.sc';
import {
  AddLabel,
  FooterSection,
  FormButtonContainer,
  FormButtonSection,
  FormDeleteContainer,
} from './listAddForm.sc';

interface OwnProps {
  inputLabelKey: string;
  addTextKey: string;
  submitForm: (val: string) => Promise<any>;
  buttonTextKey?: string;
  openAndSelected?: boolean;
  initialValue?: string;
  onCancel?: () => void;
  onDelete?: () => Promise<void>;
  showTopicScale?: string;
  isSurveyScopePrivate?: boolean;
}

type Props = OwnProps;

const ListAddForm: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [value, setValue] = useState(props.initialValue || '');
  const [error, setError] = useState<string | undefined>(undefined);

  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const formInput = useRef<HTMLInputElement | null>(null);
  const [showForm, setShowForm] = useState(props.openAndSelected);
  const [showScaleModal, setShowScaleModal] = useState(false);
  const scale = useSelector<StoreI, Scale | undefined>((state) => {
    if (!props.showTopicScale) return undefined;

    const topic = state.topics.byId[props.showTopicScale];

    return state.scales.byId[topic.scaleId];
  });

  useEffect(() => {
    const currentInput = formInput.current;
    if (currentInput && props.openAndSelected) {
      currentInput.focus();
    }
  }, [showForm, props.openAndSelected]);

  function onKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    const key = e.charCode || e.keyCode || 0;
    if (key === 13) {
      e.preventDefault();
      submitForm();
      return false;
    }
  }

  let isUnmounted = false;

  useEffect(() => {
    isUnmounted = false;
    return () => {
      isUnmounted = true;
    };
  }, []);

  function onChangeEvent(e: React.ChangeEvent<HTMLInputElement>) {
    setError(undefined);
    setValue(e.currentTarget.value);
  }

  function submitForm() {
    const trimmedValue = value.trim();
    setLoadingAdd(true);
    if (trimmedValue) {
      props
        .submitForm(trimmedValue)
        .then(() => {
          setValue('');
          setShowForm(false);
        })
        .catch((err) => {
          if (err.error && err.error.response.data[0]) {
            setError(err.error.response.data[0].translation_key);
          } else {
            setError('unknown');
          }
        })
        .finally(() => {
          setLoadingAdd(false);
        });
    } else {
      setLoadingAdd(false);
      setError('required');
    }
  }

  function blurInput() {
    setError(undefined);
  }

  function openForm() {
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setValue('');
    if (props.onCancel) {
      props.onCancel();
    }
  }

  async function onDelete() {
    if (props.onDelete) {
      setLoadingDelete(true);
      await props.onDelete();
      if (!isUnmounted) {
        setLoadingDelete(false);
      }
    }
  }

  const setInput = (ref: HTMLInputElement) => {
    formInput.current = ref;
  };

  return (
    <>
      {scale ? (
        <SC.DialogStyled show={showScaleModal} onHide={() => setShowScaleModal(!showScaleModal)} width={800}>
          <SC.ScaleModalHeader>
            <span data-translation="x.topics.details.scale.list.modal.title">
              {t('x.topics.details.scale.list.modal.title')}
            </span>
            <IconContainer>
              <HoverInfoIcon
                infoText={{
                  key: 'x.topics.details.scale.list.header.textbox',
                  txt: t('x.topics.details.scale.list.header.textbox'),
                }}
              />
            </IconContainer>
          </SC.ScaleModalHeader>
          <SC.ScaleContainer>
            <ScaleComponent
              t={t} 
              labels={
                scale.visualization === ScaleVisualization.Labels ? scale.labels : scale.multipleChoiceOptions
              }
              rating={null}
              type={scale.visualization}
              inContainer
              isMultipleChoiceEnabled={scale.isMultipleChoiceEnabled}
            />
          </SC.ScaleContainer>
        </SC.DialogStyled>
      ) : null}
      {showForm || props.openAndSelected ? (
        <div>
          <Row>
            <Col lg={24} style={{ marginTop: '15px' }}>
              <CustomFormInput
                setInput={setInput}
                label={t(props.inputLabelKey)}
                onKeyPress={onKeyPress}
                onChange={onChangeEvent}
                onBlur={blurInput}
                value={value}
                error={error}
                labelKey={props.inputLabelKey}
              />
            </Col>
          </Row>
          <FormButtonSection>
            <FormDeleteContainer>
              {props.onDelete && (
                <SecondaryButtonLoading
                  isLoading={loadingDelete}
                  type="button"
                  $buttonColor={ButtonColorTypes.Reddish}
                  onClick={onDelete}
                >
                  <span data-translation="x.list_add.delete">{t('x.list_add.delete')}</span>
                </SecondaryButtonLoading>
              )}
            </FormDeleteContainer>
            <FormButtonContainer>
              <SecondaryButton type="button" onClick={closeForm}>
                <span data-translation="x.list_add.cancel">{t('x.list_add.cancel')}</span>
              </SecondaryButton>
            </FormButtonContainer>
            <FormButtonContainer>
              <PrimaryButtonLoading type="button" isLoading={loadingAdd} onClick={submitForm}>
                <span data-translation={props.buttonTextKey || 'x.list_add.add'}>
                  {t(props.buttonTextKey || 'x.list_add.add')}
                </span>
              </PrimaryButtonLoading>
            </FormButtonContainer>
          </FormButtonSection>
        </div>
      ) : (
        <FooterSection>
          {!props.isSurveyScopePrivate && (
            <AddLabel data-translation={props.addTextKey} onClick={openForm}>
              <span>+</span> {t(props.addTextKey)}
            </AddLabel>
          )}

          {scale ? (
            <AddLabel
              data-translation={'x.questions.label.show_scale'}
              onClick={() => setShowScaleModal(!showScaleModal)}
            >
              {t('x.questions.label.show_scale')}
            </AddLabel>
          ) : null}
        </FooterSection>
      )}
    </>
  );
};

export default ListAddForm;
